import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, Link, useNavigate, Outlet, useOutletContext } from 'react-router-dom';
import { useForm } from 'react-hook-form'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'
import Container from '@mui/material/Container'
import Skeleton from '@mui/material/Skeleton'
import IconButton from '@mui/material/IconButton'
import ListItemButton from '@mui/material/ListItemButton'
import ClearIcon from '@mui/icons-material/Clear'
import TextField from '@mui/material/TextField'
import CircularProgress from '@mui/material/CircularProgress'
import LoadingButton from '@mui/lab/LoadingButton'
import AddIcon from '@mui/icons-material/Add'
import SaveIcon from '@mui/icons-material/Save'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'

import { Error, Spinner } from '../../common/Error'
import TopBar from '../../common/TopBar'
import Avatar from '../../common/Avatar'
import socket from '../../app/socket'
import { MessageWindow, MessageInput, messages_skeleton } from './Message'
import { getName } from './util'

import {
  useGetRoomQuery,
  useListRoomsQuery,
  useCreateRoomMutation,
  useLeaveRoomMutation,
  useAddMembersToRoomMutation,
  selectAllRooms,
  selectRoomById,
} from '../../app/services/rooms/roomsService'
import useSocketConnect from '../../hooks/useSocketConnect'
import {
  useListMessagesQuery,
} from '../../app/services/messages/messagesService'
import {
  selectUser
} from '../auth/authSlice'
import {
  selectAddMembersUsers,
  lookUpUserByEmail,
  reset as resetAddMembers,
  addMember,
  removeMemberByEmail,
} from './addMembersSlice'
import {
  showSnackbar,
} from '../snackbar/snackbarSlice'
import styles from './Room.module.css';

export const Room = ({ id }) => {
  const navigate = useNavigate()
  const user = useSelector(selectUser) 
  const room = useSelector((state) => selectRoomById(state, id))

  //console.log('[Room] room =', room)

  if (!room || !user) {
    return (
      <Box
        sx={{
          flexGrow: 1,
          height: '100%',
        }}
      >
        <TopBar
          title={ 'Loading...' }
          menuItems={ [] }
          backOnClick={() => navigate('/rooms')}
        />
        {messages_skeleton}
      </Box>
    )
  }

  return <Outlet context={[room]} />
}
        //<Skeleton variant='rectangular' width='100%' height='50px' />

const emptyArray = []

export const RoomChat = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const messageWindowRef = useRef(null)
  const [room] = useOutletContext()
  const user = useSelector(selectUser)
  const [open, setOpen] = React.useState(false)
  const [leaveRoom] = useLeaveRoomMutation()
  const {
    messages,
    isSuccess,
    isError,
    error,
    refetch,
  } = useListMessagesQuery(room.id, {
    selectFromResult: ({ data }) => {
      const num_messages = data?.ids.length || 0
      if (num_messages === 0) { return { messages: emptyArray } }
      return { 
        messages: data?.ids.map((id) => data?.entities[id])
      }
    }
  })

  useSocketConnect({
    name: 'RoomChat.listMessages',
    callback: () => {
      console.log('[RoomChat.listMessages] refetch')
      refetch()
    }
  })

  //console.log('[RoomChat] room=', room)

  const handleClickOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleLeave = () => {
    leaveRoom(room.id)
    setOpen(false)
    navigate('/rooms')
  }

  const handleFocusInput = (e) => {
    setTimeout(() => {
      messageWindowRef.current.scrollTop = messageWindowRef.current.scrollHeight;
    }, 100)
  }

  //console.log('[RoomChat]', room.id)

  const menu_items = [
    {
      name: 'Add members',
      onClick: () => navigate(`/rooms/${room.id}/settings`)
    },
    {
      name: 'Share link',
      onClick: () => {
        const currentUrl = window.location.href;
        navigator.clipboard.writeText(currentUrl)
          .then(() => {
            dispatch(showSnackbar({
              message: 'Copied link to clipboard!',
              severity: 'success'
            }));
          })
          .catch(() => {
            dispatch(showSnackbar({
              message: 'Failed to copy link to clipboard',
              severity: 'error'
            }));
          });
      }
    },
    {
      name: 'Reload messages',
      onClick: refetch
    },
    {
      name: 'Leave channel',
      onClick: handleClickOpen
    }
  ]

  const name = getName(room, user)

  return (
    <Box
      sx={{
        flexGrow: 1,
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <TopBar
        title={ name }
        menuItems={ menu_items }
        backOnClick={() => navigate('/rooms')}
      />
      <MessageWindow room={room} messages={messages} ref={messageWindowRef} />
      <MessageInput room={room} handleFocusInput={handleFocusInput} />
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {`Leave ${name}?`}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to leave this channel. You will not be able
            to rejoin it unless invited by another member.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleLeave} variant='contained' autoFocus>
            Leave Channel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export const RoomWrapper = () => {
  const { id } = useParams()
  return <Room id={id} />
}

export const RoomSettings = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const user = useSelector(selectUser)
  const { id } = useParams()
  const [room] = useOutletContext()
  const invitees = useSelector(selectAddMembersUsers)
  const [open, setOpen] = React.useState(false)

  const [addMembersToRoom, { isLoading, isSuccess }] = useAddMembersToRoomMutation()
  
  const {
    handleSubmit,
    register,
    formState: { errors, isDirty }
  } = useForm({
    defaultValues: {
      name: room.name
    }
  })

  //console.log('[RoomSettings] invitees=', invitees)

  const submitForm = (e) => {
    addMembersToRoom({ id, invitees })
    dispatch(resetAddMembers())
  }
  
  const handleClose = () => {
    setOpen(false)
  }

  const handleLeave = () => {
    setOpen(false)
    navigate(`/rooms/${id}`)
    dispatch(resetAddMembers())
  }

  const isDisabled = isLoading || invitees.length === 0

  const handleBackClick = () => {
    if (isDirty || invitees.length > 0) {
      setOpen(true) 
    } else {
      navigate(`/rooms/${id}`)
    }
  }

  return (
    <Box>
      <Box
        component='form'
        noValidate='noValidate'
        onSubmit={handleSubmit(submitForm)}
      >
        <TopBar
          title={ getName(room, user) }
          primaryAction={
            <LoadingButton
              size='medium'
              type='submit'
              variant='contained'
              loading={isLoading}
              loadingPosition="start"
              startIcon={<SaveIcon />}
              disabled={isDisabled}
            >
              Save
            </LoadingButton>
          }
          backOnClick={handleBackClick}
        />
      </Box>
      <MembersList initialMembers={room.users} />
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {'Discard new members?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            You have uninvited members in the list. If you discard them,
            they will not be invited to the channel.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleLeave}>
            Discard members
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
//  <TextField
//    id='name'
//    name='name'
//    disabled={true}
//    {...register('name')}
//    fullWidth
//    label='Name'
//    error={errors.name ? true : false}
//    helperText={errors.name?.message}
//  />

export const MembersListItem = ({ name, email, is_anonymous, onDelete }) => {
  //console.log('MembersListItem', name, email, is_anonymous, onDelete)

  return (
    <ListItem>
      <ListItemAvatar>
        {is_anonymous
          ? <Avatar size="small" icon={<MarkEmailReadIcon />} name={email} />
          : <Avatar size="small" name={name} />
        }
      </ListItemAvatar>
      <ListItemText>
        {is_anonymous ? email : name }
      </ListItemText>
      {onDelete && (
        <IconButton
          onClick={() => onDelete({ name, email, is_anonymous })}
          sx={{
            color: 'grey.500'
          }}
        >
          <ClearIcon /> 
        </IconButton>
      )}
    </ListItem>
  )
}

// TODO: Redo this as a real form with schema validation
export const NewMemberItem = ({ existingMembers }) => {
  const dispatch = useDispatch()
  const {
    users: queued_members,
    loading, error, success
  } = useSelector((state) => state.add_members)
  const [open, setOpen] = React.useState(false)
  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required('Email is required')
      .email('Email is invalid')
      .test({
        name: 'is-new',
        skipAbsent: true,
        test(value, ctx) {
          //console.log('[is-new test]', value, queued_members)
          if (existingMembers.map((u) => u.email).includes(value)) {
            return ctx.createError({ message: 'User is already in the channel' })
          }
          if (queued_members.map((u) => u.email).includes(value)) {
            return ctx.createError({ message: 'Email already will be invited' })
          }
          return true
        }
      })
  })
  const {
    register: registerNewMember,
    handleSubmit: handleSubmitNewMember,
    formState: { isDirty, errors },
    reset
  } = useForm({
    defaultValues: {
      email: ''
    },
    resolver: yupResolver(validationSchema)
  })

  const handleClose = () => {
    setOpen(false)
    reset()
  }

  useEffect(() => {
    if (success) {
      setOpen(false)
      reset()
    }
  }, [success])

  const submitForm = (data, e) => {
    dispatch(lookUpUserByEmail(data.email.toLowerCase()))
  }

  //console.log('[NewMemberItem]', loading, error, success)
  //console.log('[NewMemberItem] errors:', errors)
  return (
    <React.Fragment>
      <ListItem>
        <ListItemButton
          onClick={() => setOpen(true)}
          sx={{
            px: 0
          }}
        >
          <ListItemAvatar>
            <Avatar
              icon={<AddIcon />}
              name={'Add member'}
              sx={{
                padding: 0,
                backgroundColor: 'inherit',
                border: '2px solid #eeeeee',
                color: '#eeeeee',
              }}
            />
          </ListItemAvatar>
          <ListItemText>
            Add member
          </ListItemText>
        </ListItemButton>
      </ListItem>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        component='form'
        noValidate='noValidate'
        onSubmit={handleSubmitNewMember(submitForm)}
      >
        <DialogTitle id="alert-dialog-title">
          Add member
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description"
            sx={{
              pb: 3
            }}
          >
            Invite new members by email address.
          </DialogContentText>
          <TextField
            id='email'
            name='email'
            disabled={loading}
            {...registerNewMember('email')}
            required
            fullWidth
            autoFocus
            label='Email'
            error={errors.email ? true : false}
            helperText={errors.email?.message}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <LoadingButton
            type='submit'
            variant='contained'
            loading={loading}
            loadingPosition="start"
            startIcon={<AddIcon />}
          >
            Add member
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  )
}

export const MembersList = ({ initialMembers, onDeleteMember }) => {
  const dispatch = useDispatch()
  const invitees = useSelector(selectAddMembersUsers)

  //console.log('[MembersList] initialMembers', initialMembers, ' invitees =', invitees)

  const handleDeleteMember = (email) => {
    dispatch(removeMemberByEmail(email))
  }

  return (
    <Box sx={{ mt: 5, mb: 5 }}>
      <Container>
        <Typography
          variant='h6'
          component='h2'
        >
          Members
        </Typography>
      </Container>
      <List>
        <NewMemberItem existingMembers={initialMembers} />
        {invitees.map((u, j) => {
          return (
            <MembersListItem
              key={initialMembers.length+j}
              {...u} 
              onDelete={handleDeleteMember}
            />
          )
        })}
        {initialMembers.map((u, j) => {
          return <MembersListItem key={j} {...u} />
        })}
      </List>
    </Box>
  )
}

export const NewRoom = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const user = useSelector(selectUser)
  const invitees = useSelector(selectAddMembersUsers)
  const inputRef = useRef(null)
  const [createRoom, { data, isLoading, isSuccess, error }] = useCreateRoomMutation()
  const {
    handleSubmit,
    register,
    formState: { errors }
  } = useForm({
    defaultValues: {
      name: ''
    }
  })

  useEffect(() => {
    if (isSuccess) {
      dispatch(resetAddMembers())
      navigate(`/rooms/${data.ids[0]}`)
    }
  }, [navigate, isSuccess])

  const submitForm = (data) => {
    createRoom({
      name: data.name,
      emails: invitees.map(invitee => invitee.email)
    })
  }

  return (
    <Box>
      <Box
        component='form'
        noValidate='noValidate'
        onSubmit={handleSubmit(submitForm)}
      >
        <TopBar
          title='New channel'
          primaryAction={
            <LoadingButton
              size='medium'
              type='submit'
              variant='contained'
              loading={isLoading}
              loadingPosition="start"
              startIcon={<SaveIcon />}
            >
              Save
            </LoadingButton>
          }
        />
      </Box>
      <Container sx={{ mt: '30px' }}>
        <TextField
          id='name'
          name='name'
          {...register('name')}
          fullWidth
          label='Name'
          error={errors.name ? true : false}
          helperText={errors.name?.message}
        />
      </Container>
      <MembersList
        initialMembers={[user]}
      />
    </Box>
  )
}

